import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaFlag } from "react-icons/fa";

// ── Real profile images
import p1 from "../../assets/p1.jpeg";
import p2 from "../../assets/p2.jpeg";
import p3 from "../../assets/p3.jpeg";
import p4 from "../../assets/p4.jpeg";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,600;1,300;1,600&family=DM+Sans:wght@300;400;500&display=swap');
  @keyframes goldShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { overflow-x: hidden; }
`;

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.06); }
`;

// ── Layout
const Wrapper = styled.div`
  position: relative;
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(
    160deg,
    #0a000f 0%,
    #130025 40%,
    #200040 70%,
    #0f0020 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  pointer-events: none;
  opacity: ${({ op }) => op || 0.3};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

const Scene = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  /* Tablet */
  @media (max-width: 1023px) {
    padding: 1.5rem;
    gap: 0;
  }

  /* Mobile: stack vertically */
  @media (max-width: 767px) {
    flex-direction: column;
    justify-content: center;
    gap: 0;
    padding: 1rem 1.25rem;
  }
`;

const StackArea = styled.div`
  position: relative;
  flex-shrink: 0;

  /* Desktop */
  width: 340px;
  height: 480px;

  /* Tablet */
  @media (max-width: 1023px) {
    width: 260px;
    height: 370px;
  }

  /* Mobile */
  @media (max-width: 767px) {
    width: min(280px, 80vw);
    height: min(396px, calc(80vw * 1.415));
  }

  @media (max-width: 400px) {
    width: 240px;
    height: 340px;
  }
`;

const Card = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  overflow: hidden;
  transform-origin: bottom center;
  cursor: pointer;
  will-change: transform, opacity, filter;
  --r: ${({ rot }) => rot || "0deg"};
  transform: rotate(var(--r)) translateY(${({ oy }) => oy || "0px"});
`;

const CardImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: blur(${({ bluramt }) => bluramt || "0px"})
    brightness(${({ dark }) => (dark ? 1 - dark * 0.45 : 1)});
`;

const CardNoise = styled.div`
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
  mix-blend-mode: overlay;
  pointer-events: none;
  z-index: 1;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 35%,
    rgba(0, 0, 0, 0.82) 100%
  );
  z-index: 2;
`;

const FlagBadge = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 100px;
  background: rgba(200, 30, 30, 0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 100, 100, 0.35);
  box-shadow: 0 2px 12px rgba(200, 30, 30, 0.4);
`;

const FlagIconWrap = styled.div`
  color: #fff;
  font-size: 0.6rem;
  display: flex;
  align-items: center;
`;

const FlagNum = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
`;

const ZeroRizzBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 6;
  padding: 5px 12px;
  border-radius: 100px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 210, 60, 0.55);
  background: linear-gradient(
    90deg,
    rgba(160, 120, 30, 0.55),
    rgba(255, 215, 0, 0.6),
    rgba(200, 160, 40, 0.55),
    rgba(255, 215, 0, 0.6),
    rgba(160, 120, 30, 0.55)
  );
  background-size: 200% auto;
  animation: goldShimmer 2.5s linear infinite;
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #fff8dc;
  text-shadow: 0 0 10px rgba(255, 220, 80, 0.6);
  box-shadow: 0 2px 14px rgba(200, 160, 40, 0.35);
  white-space: nowrap;
`;

const RizzBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 6;
  padding: 5px 12px;
  border-radius: 100px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.1);
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.75);
  white-space: nowrap;
`;

const MatchBadge = styled.div`
  position: absolute;
  top: 52px;
  right: 14px;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.2),
    rgba(124, 58, 237, 0.2)
  );
  border: 1px solid rgba(232, 121, 249, 0.5);
  border-radius: 100px;
  padding: 0.3rem 0.75rem;

  span {
    font-family: "Mendl", sans-serif;
    font-size: 0.6rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #e879f9;
  }

  div {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #e879f9;
    box-shadow: 0 0 8px #e879f9;
    animation: ${pulse} 1.5s ease-in-out infinite;
  }
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.75rem;
  z-index: 3;

  @media (max-width: 1023px) {
    padding: 1.25rem;
  }
`;

const CardName = styled.div`
  font-family: "Mendl", serif;
  font-size: clamp(1.4rem, 3.5vw, 2rem);
  font-weight: 600;
  color: rgba(255, 255, 255, ${({ op }) => op || 0.4});
  line-height: 1;
  margin-bottom: 0.3rem;
`;

const CardMeta = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.8rem;
  font-weight: 300;
  color: rgba(255, 255, 255, ${({ op }) => op || 0.2});
  letter-spacing: 0.05em;
`;

const CardTags = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  opacity: ${({ op }) => op || 0};
  transform: translateY(${({ ty }) => ty || "8px"});
  transition:
    opacity 0.5s,
    transform 0.5s;
`;

const Tag = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  padding: 0.25rem 0.6rem;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
`;

// On desktop: floats to the left of the stack.
// On mobile: sits centered below the stack.
const SwipeRight = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0.85;

  /* Desktop / Tablet */
  @media (min-width: 768px) {
    top: 50%;
    left: -70px;
    transform: translateY(-50%);
  }

  /* Mobile: below the stack, clear the dot counter */
  @media (max-width: 767px) {
    top: calc(100% + 2.2rem);
    left: 50%;
    transform: translateX(-50%);
  }
`;

const SwipeArrow = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 0 30px rgba(232, 121, 249, 0.5);
`;

const SwipeLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
`;

const TextPanel = styled.div`
  max-width: 700px;
  flex-shrink: 1;

  /* Desktop */
  @media (min-width: 1024px) {
    margin-left: 7rem;
  }

  /* Tablet */
  @media (min-width: 768px) and (max-width: 1023px) {
    margin-left: 3rem;
  }

  /* Mobile: below stack, extra top margin for swipe arrow + dots */
  @media (max-width: 767px) {
    margin-left: 0;
    margin-top: 7rem;
    text-align: center;
    width: 100%;
    max-width: 100%;
  }
`;

const Eyebrow = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: #c084fc;
  margin-bottom: 1.25rem;
  opacity: 0;
  transform: translateY(20px);
`;

const Headline = styled.h2`
  font-family: "Mendl", serif;
  font-size: clamp(2rem, 4.5vw, 4.5rem);
  font-weight: 300;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(25px);
  max-width: 1700px;
  overflow: visible;

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(90deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
    overflow: visible;
  }
`;

const Body = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.85rem, 1.8vw, 1rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.85;
  margin-bottom: 2.5rem;
  opacity: 0;
  transform: translateY(20px);
`;

const CardCounter = styled.div`
  position: absolute;
  bottom: -2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.div`
  width: ${({ active }) => (active ? "20px" : "6px")};
  height: 6px;
  border-radius: 3px;
  background: ${({ active }) =>
    active
      ? "linear-gradient(90deg, #a855f7, #e879f9)"
      : "rgba(255,255,255,0.15)"};
  transition:
    width 0.4s,
    background 0.4s;
`;

// ── Data
const CARDS = [
  {
    name: "Chris, 24",
    meta: "Brooklyn · Designer",
    tags: ["Art", "Coffee", "Hiking"],
    img: p1,
    bluramt: "0px",
    dark: 0.35,
    rot: "6deg",
    oy: "-8px",
    nameOp: 0.3,
    metaOp: 0.15,
    flagCount: 2,
    topRight: { count: 12 },
  },
  {
    name: "Maya, 27",
    meta: "Manhattan · Architect",
    tags: ["Travel", "Jazz", "Yoga"],
    img: p2,
    bluramt: "0px",
    dark: 0.22,
    rot: "-4deg",
    oy: "-4px",
    nameOp: 0.45,
    metaOp: 0.28,
    flagCount: null,
    topRight: "zerorizz",
  },
  {
    name: "Jeff, 45",
    meta: "Williamsburg · Photographer",
    tags: ["Film", "Music", "Surf"],
    img: p3,
    bluramt: "0",
    dark: 0.12,
    rot: "2deg",
    oy: "0px",
    nameOp: 0.65,
    metaOp: 0.42,
    flagCount: 1,
    topRight: { count: 7 },
  },
  {
    name: "Amanda, 26",
    meta: "SoHo · Writer",
    tags: ["Books", "Wine", "Dance"],
    img: p4,
    bluramt: "0px",
    dark: 0,
    rot: "0deg",
    oy: "0px",
    nameOp: 1,
    metaOp: 1,
    flagCount: null,
    topRight: "zerorizz",
  },
];

export default function CardStackReveal() {
  const wrapperRef = useRef(null);
  const cardRefs = useRef([]);
  const dotRefs = useRef([]);
  const eyebrowRef = useRef(null);
  const headlineRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const vh = window.innerHeight;
    const scrollDist = vh * (CARDS.length + 1.5);
    wrapper.style.height = `${scrollDist + vh}px`;

    gsap
      .timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: "top top",
          end: `+=${vh * 0.6}`,
          scrub: 0.7,
        },
      })
      .to(eyebrowRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
      .to(headlineRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.1)
      .to(bodyRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2);

    [3, 2, 1].forEach((cardIdx, peelStep) => {
      const card = cardRefs.current[cardIdx];
      if (!card) return;
      const start = vh * (0.8 + peelStep * 1.1);
      const end = vh * (1.7 + peelStep * 1.1);

      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: `+=${start}`,
            end: `+=${end}`,
            scrub: 0.9,
          },
        })
        .to(card, {
          x: cardIdx % 2 === 0 ? "130%" : "-130%",
          y: "-20%",
          rotation: cardIdx % 2 === 0 ? "25deg" : "-25deg",
          opacity: 0,
          scale: 0.92,
          ease: "power2.inOut",
        });

      ScrollTrigger.create({
        trigger: wrapper,
        start: `+=${(start + end) / 2}`,
        onEnter: () => updateDots(3 - peelStep - 1),
        onLeaveBack: () => updateDots(3 - peelStep),
      });
    });

    function updateDots(activeIdx) {
      dotRefs.current.forEach((dot, i) => {
        if (!dot) return;
        dot.style.width = i === activeIdx ? "20px" : "6px";
        dot.style.background =
          i === activeIdx
            ? "linear-gradient(90deg,#a855f7,#e879f9)"
            : "rgba(255,255,255,0.15)";
      });
    }
    updateDots(3);

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper ref={wrapperRef}>
        <Sticky>
          <Orb
            s="500px"
            c="rgba(124,58,237,0.2)"
            top="-10%"
            left="-8%"
            dur="6s"
            del="0s"
            op={0.35}
          />
          <Orb
            s="400px"
            c="rgba(232,121,249,0.15)"
            top="40%"
            right="-6%"
            dur="7s"
            del="1s"
            op={0.25}
          />
          <Orb
            s="300px"
            c="rgba(168,85,247,0.18)"
            bottom="-5%"
            left="35%"
            dur="5s"
            del="0.5s"
            op={0.3}
          />

          <Scene>
            <StackArea>
              {CARDS.map((card, i) => (
                <Card
                  key={i}
                  ref={(el) => (cardRefs.current[i] = el)}
                  rot={card.rot}
                  oy={card.oy}
                  style={{ zIndex: i + 1 }}
                >
                  <CardImg
                    src={card.img}
                    alt={card.name}
                    draggable="false"
                    bluramt={card.bluramt}
                    dark={card.dark}
                  />
                  <CardNoise />
                  <CardOverlay />

                  {card.flagCount !== null && (
                    <FlagBadge>
                      <FlagIconWrap>
                        <FaFlag />
                      </FlagIconWrap>
                      <FlagNum>{card.flagCount}</FlagNum>
                    </FlagBadge>
                  )}

                  {card.topRight === "zerorizz" && (
                    <ZeroRizzBadge>✦ Zero Rizz</ZeroRizzBadge>
                  )}
                  {card.topRight?.count !== undefined && (
                    <RizzBadge>Rizz: {card.topRight.count}</RizzBadge>
                  )}

                  {i === CARDS.length - 1 && (
                    <MatchBadge>
                      <div />
                      <span>Your Match</span>
                    </MatchBadge>
                  )}

                  <CardContent>
                    <CardName op={card.nameOp}>{card.name}</CardName>
                    <CardMeta op={card.metaOp}>{card.meta}</CardMeta>
                    <CardTags
                      op={i === CARDS.length - 1 ? 1 : 0}
                      ty={i === CARDS.length - 1 ? "0px" : "8px"}
                    >
                      {card.tags.map((t, j) => (
                        <Tag key={j}>{t}</Tag>
                      ))}
                    </CardTags>
                  </CardContent>
                </Card>
              ))}

              <SwipeRight>
                <SwipeArrow>❤️</SwipeArrow>
                <SwipeLabel>It's a match</SwipeLabel>
              </SwipeRight>

              <CardCounter>
                {CARDS.map((_, i) => (
                  <Dot
                    key={i}
                    ref={(el) => (dotRefs.current[i] = el)}
                    active={i === CARDS.length - 1}
                  />
                ))}
              </CardCounter>
            </StackArea>

            <TextPanel>
              <Eyebrow ref={eyebrowRef}>The Discover page</Eyebrow>
              <Headline ref={headlineRef}>
                When modern dating culture
                <br />
                <em>turns into a game!</em>
              </Headline>
              <Body ref={bodyRef}>
                See red flags before you engage, and red flag toxic behavior to
                keep the space safer for everyone. Start fresh with your Zero Rizz,
                showing a clean slate. Rizz, match, connect — and your Rizz Score updates
                automatically. It's a playful system that keeps attraction
                honest, visible, and accountable for all users.
              </Body>
            </TextPanel>
          </Scene>
        </Sticky>
      </Wrapper>
    </>
  );
}
